import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ChatService } from "../services/chat/chatService";
import { isRevealRequest } from "../utils/revealUtils";
import { isFeedbackRequest } from "../utils/feedbackUtils";
import { useAuth } from "./AuthContext";
import { ChatContextType, Message, ProcessedFile } from "./types";
import { useSessionManager } from "./hooks/useSessionManager";
import { useFeedbackHandler } from "./hooks/useFeedbackHandler";
import { useRevealHandler } from "./hooks/useRevealHandler";
import { useTargetGeneration } from "./hooks/useTargetGeneration";
import { useEnhancedFeedbackHandler } from "./hooks/useEnhancedFeedbackHandler";
import { isImpressionNote } from "./utils/messageUtils";
import { extractTargetFromResponse, storeTargetSafely, generateTargetByType, getTargetTypeFromRequest, isTargetGenerationRequest } from "../utils/targetGenerationUtils";
import { useToast } from "@/hooks/use-toast";
import { chatApi } from "../services/chatApiService";
import { useTokenWarnings } from "../hooks/useTokenWarnings";
import TokenDepletionModal from "../components/tokens/TokenDepletionModal";
import TokenPurchaseModal from "../components/tokens/TokenPurchaseModal";
import { useTokens } from "../hooks/useTokens";
import { uploadFileToSupabase } from "../lib/supabase/storage";

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Enhanced function to detect feedback responses by their unique markdown headers
const isFeedbackAnalysis = (text: string): boolean => {
  if (!text || typeof text !== 'string') {
    return false;
  }
  
  const feedbackMarkers = [
    '## 📊', '## ✅', '## ❌', '## 🔄', '## 💡',
    'Impressions Overview', 'Accurate Impressions', 'Analysis of User Notes',
    'Accurate Elements', 'Inaccurate Elements', 'Partial Matches', 'Target Details'
  ];
  return feedbackMarkers.some(marker => text.includes(marker));
};

// ENHANCED: More comprehensive filter for internal analysis requests and tool messages
const isInternalAnalysisMessage = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  // Log the content being tested for debugging
  console.log('🔍 FILTER: Testing content for internal analysis patterns:', content.substring(0, 200));
  
  const internalRequestPatterns = [
    // Original patterns for analysis requests
    /^Analyze remote viewing session for Target [A-Z0-9]{4}-[A-Z0-9]{4}\./,
    /^Target [A-Z0-9]{4}-[A-Z0-9]{4} revealed\./,
    // Additional patterns for internal prompts that may leak
    /^TARGET:\s*(Type:|https?:)/,
    /^USER_IMPRESSIONS:/,
    /^ANALYSIS_REQUEST:/,
    /^Please analyze the following remote viewing session/,
    /^Based on the target details and user impressions/,
    // Filter tool messages and internal processing
    /^\[TOOL_CALL\]/,
    /^\[INTERNAL_ANALYSIS\]/,
    /^SYSTEM_PROMPT:/,
    // Filter any message that contains internal analysis structure patterns
    /TARGET:.*TYPE:.*USER'S IMPRESSIONS:/s,
    /^\[INTERNAL_ANALYSIS\]\s*Analyze remote viewing session/,
    // Enhanced patterns to catch variations
    /^TARGET:.*USER'S IMPRESSIONS:/s,
    /^Analyze remote viewing session/,
    /CRITICAL INSTRUCTION: Use ONLY second person language/,
    /VISION_ANALYSIS_REQUEST/,
    /IMAGE_COUNT:/,
    /Examples of CORRECT language:/,
    /Examples of INCORRECT language/
  ];

  const isInternal = internalRequestPatterns.some(pattern => pattern.test(content));
  
  if (isInternal) {
    console.log('🚫 FILTER: BLOCKED internal analysis message:', content.substring(0, 150));
  }
  
  return isInternal;
};

// Helper to convert base64 to File object
const base64ToFile = (base64String: string, filename: string): File => {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// NEW: Helper to merge optimistic and persistent messages
const mergeOptimisticWithPersistent = (
  optimisticMessage: Message, 
  persistentMessage: any
): Message => {
  console.log('🔄 MERGE: Merging optimistic with persistent message:', {
    optimisticId: optimisticMessage.id,
    persistentId: persistentMessage.id,
    optimisticFiles: optimisticMessage.files?.length || 0,
    persistentFileUrls: persistentMessage.file_urls?.length || 0
  });

  // Keep the optimistic base64 files for immediate display
  // Add the persistent URLs for storage reference
  const mergedFiles = [
    ...(optimisticMessage.files || []),
    ...(persistentMessage.file_urls || []).map(url => ({
      type: 'image' as const,
      content: url,
      filename: url.split('/').pop() || 'image.jpg'
    }))
  ];

  // Remove duplicates based on filename
  const uniqueFiles = mergedFiles.filter((file, index, arr) => 
    arr.findIndex(f => f.filename === file.filename) === index
  );

  console.log('🔄 MERGE: Merged files result:', {
    totalFiles: uniqueFiles.length,
    fileTypes: uniqueFiles.map(f => ({
      filename: f.filename,
      isBase64: f.content.startsWith('data:image/'),
      isUrl: f.content.startsWith('http')
    }))
  });

  return {
    ...optimisticMessage,
    id: persistentMessage.id, // Use the persistent ID
    files: uniqueFiles
  };
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTokenDepletionModal, setShowTokenDepletionModal] = useState(false);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState<Map<string, Message>>(new Map());
  const [isNewSession, setIsNewSession] = useState(false); // Track if this is a newly created session (not default)
  const [showingInitialWelcome, setShowingInitialWelcome] = useState(false); // Track if showing immediate welcome
  const { toast } = useToast();
  const { user } = useAuth();
  const { tokenBalance } = useTokens();

  // Use stable target generation hook
  const {
    isGenerating: generatingTargetType,
    generatingType,
    startGeneration,
    completeGeneration,
    resetGeneration
  } = useTargetGeneration();

  const {
    sessions,
    currentSessionId,
    currentTargetCode,
    setCurrentTargetCode,
    loadSessions,
    createNewSession: createSession,
    switchSession,
    clearSession
  } = useSessionManager();

  const { handleFeedbackRequest } = useFeedbackHandler();
  const { handleRevealRequest } = useRevealHandler();
  const { handleTargetAnalysisRequest } = useEnhancedFeedbackHandler();

  // Track if target has been revealed for this session
  const [targetIsRevealed, setTargetIsRevealed] = useState<boolean>(false);
  
  // Track recent confirmation messages to prevent duplicates (simple time-based deduplication)
  const [recentConfirmations, setRecentConfirmations] = useState<Set<string>>(new Set());

  // FIXED: Token warnings integration with proper initialization checking
  const {
    isTokenDepleted,
    checkTokensForRequest,
    getInsufficientTokensMessage,
    isInitialized: tokenWarningsInitialized
  } = useTokenWarnings({
    onTokenDepletionModal: () => {
      // FIXED: Only show modal if tokens are initialized and actually depleted
      if (tokenWarningsInitialized && tokenBalance <= 0) {
        console.log('💰 ChatContext: Showing token depletion modal for balance:', tokenBalance);
        setShowTokenDepletionModal(true);
      } else {
        console.log('💰 ChatContext: Skipping modal - not initialized or balance ok:', { isInitialized: tokenWarningsInitialized, tokenBalance });
      }
    }
  });

  // Add insufficient tokens message to chat when tokens are depleted
  const addInsufficientTokensMessage = (estimatedTokens: number = 500) => {
    const insufficientMessage: Message = {
      id: `insufficient-tokens-${Date.now()}`,
      content: getInsufficientTokensMessage(estimatedTokens),
      type: "system",
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, insufficientMessage]);
    
    // Always open the purchase modal when tokens are insufficient
    setShowTokenPurchaseModal(true);
  };

  // Load user sessions when authenticated - only on actual user change
  useEffect(() => {
    if (user?.id) {
      // Clear any existing state to ensure fresh start
      setMessages([]);
      clearSession();
      resetGeneration();
      setTargetIsRevealed(false);
      setOptimisticMessages(new Map());
      
      // Show immediate welcome message without waiting for session loading
      const immediateWelcomeMessage: Message = {
        id: `welcome-immediate-${Date.now()}`,
        content: `## Welcome to RemoteViz! 🌍

Your journey into remote viewing starts here:

🎯 **Generate a Target**
Click a button or type "Generate Location Target" for a random Google Street View location, or "Generate Image Target" for a random image. You'll get a unique 8-digit Target Code—your gateway to the unknown.

📓 **Submit Your Impressions:**
Share your sketches and written impressions. Your notes help evaluate accuracy.

🫣 **Reveal the Target:**
Click the "Reveal Target" button or type "Reveal Target" to uncover the hidden location or image.

💯 **Get Personalized Feedback:**
We'll compare your impressions to the actual target and help you improve your skills.

🙋 **Get Support Anytime:**
Just type "help" anytime for assistance.`,
        type: "system",
        timestamp: Date.now()
      };
      
      setMessages([immediateWelcomeMessage]);
      setShowingInitialWelcome(true);
      
      // Load sessions in background (this will create the actual session)
      loadSessions();
    } else {
      setMessages([]);
      clearSession();
      resetGeneration();
      setTargetIsRevealed(false);
      setShowingInitialWelcome(false);
    }
  }, []); // Only run once on mount - don't depend on user changes

  // Load messages when a session is established (after loadSessions completes)
  useEffect(() => {
    const loadMessagesForCurrentSession = async () => {
      console.log('🔄 ChatContext: Message loading effect triggered with conditions:', {
        currentSessionId,
        isLoading,
        showingInitialWelcome,
        isNewSession,
        currentMessagesCount: messages.length,
        generatingTargetType
      });
      
      // 🛡️ CRITICAL FIX: Never replace messages during target generation
      if (generatingTargetType) {
        console.log('🔄 ChatContext: BLOCKED message loading during target generation:', generatingTargetType);
        return;
      }
      
      if (currentSessionId && !isLoading && !showingInitialWelcome && !isNewSession) {
        // 🛡️ CRITICAL: Don't reload if we have optimistic messages (like target generation)
        const hasOptimisticMessages = messages.some(m => 
          m.id?.includes('target-generated') || 
          m.id?.includes('user-') ||
          m.type === 'user'
        );
        
        if (hasOptimisticMessages) {
          console.log('🔄 ChatContext: PRESERVING optimistic messages - skipping database reload');
          console.log('🔄 ChatContext: Current optimistic messages:', messages.map(m => ({id: m.id, type: m.type, preview: m.content.substring(0, 50)})));
          return;
        }
        
        console.log('🔄 ChatContext: Loading messages for existing session:', currentSessionId);
        console.log('🔄 ChatContext: Current messages before loading:', messages.length, messages.map(m => m.id));
        
        try {
          const formattedMessages = await switchSession(currentSessionId);
          
          if (formattedMessages.length > 0) {
            console.log('🔄 ChatContext: Loaded', formattedMessages.length, 'messages from database - REPLACING CURRENT MESSAGES');
            console.log('💥 CRITICAL: About to REPLACE current messages:', {
              currentMessages: messages.length,
              currentMessageIds: messages.map(m => m.id),
              loadedMessages: formattedMessages.length,
              loadedMessageIds: formattedMessages.map(m => m.id),
              isGenerating: generatingTargetType,
              currentSessionId,
              stackTrace: new Error().stack?.split('\n').slice(1, 5)
            });
            setMessages(formattedMessages);
            setOptimisticMessages(new Map());
          } else {
            console.log('🔄 ChatContext: No messages in database for existing session - KEEPING CURRENT MESSAGES');
          }
        } catch (error) {
          console.error('❌ ChatContext: Failed to load initial messages:', error);
        }
      } else {
        console.log('🔄 ChatContext: Message loading blocked by conditions.');
      }
    };

    // Only load messages for existing sessions, not new ones, and NEVER during target generation
    if (!showingInitialWelcome && !isNewSession && !generatingTargetType) {
      console.log('🔄 ChatContext: Scheduling message load in 100ms');
      const timeoutId = setTimeout(loadMessagesForCurrentSession, 100);
      return () => clearTimeout(timeoutId);
    } else {
      console.log('🔄 ChatContext: Message loading skipped. Welcome:', showingInitialWelcome, 'New:', isNewSession, 'Generating:', generatingTargetType);
    }
  }, [currentSessionId, isLoading, showingInitialWelcome, isNewSession, generatingTargetType]);

  // Reset immediate welcome when actual messages are received (not on session creation)
  useEffect(() => {
    if (currentSessionId && showingInitialWelcome && messages.length > 1) {
      // Only reset when we have actual conversation messages (more than just welcome)
      setShowingInitialWelcome(false);
    }
  }, [currentSessionId, showingInitialWelcome, messages.length]);

  // PHASE 4: Reset target revealed state when session changes and refresh from database
  useEffect(() => {
    const loadTargetRevealState = async () => {
      if (currentSessionId) {
        try {
          const target = await ChatService.getTargetBySession(currentSessionId);
          const isRevealed = target && !!target.revealed_at;
          console.log('🎯 ChatContext: Loading reveal state for session:', {
            sessionId: currentSessionId,
            hasTarget: !!target,
            targetCode: target?.target_code,
            isRevealed,
            revealedAt: target?.revealed_at
          });
          
          if (target) {
            setCurrentTargetCode(target.target_code);
            setTargetIsRevealed(isRevealed);
          } else {
            setTargetIsRevealed(false);
          }
        } catch (error) {
          console.error('❌ ChatContext: Error loading target reveal state:', error);
          setTargetIsRevealed(false);
        }
      } else {
        setTargetIsRevealed(false);
      }
    };
    
    loadTargetRevealState();
  }, [currentSessionId]);

  // ENHANCED: Subscribe to real-time messages with improved file handling and optimistic merging
  useEffect(() => {
    if (!currentSessionId) return;

    console.log('🔄 SUBSCRIPTION: Setting up real-time subscription for session:', currentSessionId);
    
    // CRITICAL FIX: Store current messages before setting up subscription to prevent clearing them
    const currentMessagesSnapshot = messages;
    
    const subscription = ChatService.subscribeToMessages(currentSessionId, async (newMessage) => {
      console.log('🔄 SUBSCRIPTION: Raw message received:', {
        messageId: newMessage.id,
        role: newMessage.role,
        contentLength: newMessage?.content?.length || 0,
        hasFileUrls: !!newMessage.file_urls,
        fileUrlsCount: newMessage.file_urls?.length || 0,
        fileUrls: newMessage.file_urls,
        timestamp: newMessage.created_at
      });
      
      const messageContent = newMessage?.content || '';
      
      // Enhanced feedback analysis detection
      if (isFeedbackAnalysis(messageContent)) {
        console.log('🎯 SUBSCRIPTION: FEEDBACK ANALYSIS DETECTED - Processing for display');
        console.log('🔄 SUBSCRIPTION: Feedback content preview:', messageContent.substring(0, 200));
      }
      
      // CRITICAL FIX: Filter internal analysis messages from real-time subscription
      if (isInternalAnalysisMessage(messageContent)) {
        console.log('🔄 SUBSCRIPTION: Filtering internal analysis message:', messageContent.substring(0, 100));
        return;
      }
      
      // CRITICAL FIX: Skip PREDICTION_RESULT messages - they're added optimistically by addPredictionResultMessage
      // and should not be duplicated by the real-time subscription
      if (messageContent.startsWith('PREDICTION_RESULT:')) {
        console.log('🔄 SUBSCRIPTION: Skipping PREDICTION_RESULT (already added optimistically)');
        return;
      }
      
      // Filter empty messages
      if (!messageContent.trim()) {
        console.log('🔄 SUBSCRIPTION: Filtering empty message');
        return;
      }
      
      // Simple confirmation deduplication - check for recent confirmations with same content
      if (messageContent.includes('Your impression has been recorded!') || 
          messageContent.includes('Image received!')) {
        const confirmationKey = messageContent.substring(0, 50); // Use first 50 chars as key
        
        if (recentConfirmations.has(confirmationKey)) {
          console.log('🔄 SUBSCRIPTION: Filtering duplicate confirmation (already seen recently)');
          return;
        }
        
        // Add to recent confirmations and clean up after 10 seconds
        setRecentConfirmations(prev => new Set([...prev, confirmationKey]));
        setTimeout(() => {
          setRecentConfirmations(prev => {
            const updated = new Set(prev);
            updated.delete(confirmationKey);
            return updated;
          });
        }, 10000);
      }
      
      setMessages(prev => {
        const messageExists = prev.some(msg => msg.id === newMessage.id);
        if (messageExists) {
          console.log('🔄 SUBSCRIPTION: Message already exists, skipping:', newMessage.id);
          return prev;
        }
        
        // ENHANCED: Process file URLs with comprehensive validation
        let processedFiles: ProcessedFile[] = [];
        if (newMessage.file_urls && newMessage.file_urls.length > 0) {
          console.log('📦 SUBSCRIPTION: Processing file URLs:', {
            fileUrls: newMessage.file_urls,
            count: newMessage.file_urls.length
          });
          
          processedFiles = newMessage.file_urls
            .filter(url => url && (url.startsWith('http') || url.startsWith('https')))
            .map((url, index) => {
              const processedFile: ProcessedFile = {
                type: 'image' as const,
                content: url,
                filename: url.split('/').pop() || `image-${index + 1}.jpg`
              };
              
              console.log(`📦 SUBSCRIPTION: Processed file ${index + 1}:`, {
                originalUrl: url,
                filename: processedFile.filename,
                isValidUrl: processedFile.content.startsWith('http'),
                contentLength: processedFile.content.length
              });
              
              return processedFile;
            });
          
          console.log('📦 SUBSCRIPTION: Final processed files:', {
            totalCount: processedFiles.length,
            fileDetails: processedFiles.map(f => ({
              type: f.type,
              filename: f.filename,
              isUrl: f.content.startsWith('http')
            }))
          });
        }

        // FIXED: Handle optimistic message replacement for user messages
        if (newMessage.role === 'user') {
          // Look for matching optimistic message by content and timestamp proximity
          const matchingOptimisticIndex = prev.findIndex(msg => 
            msg.type === 'user' && 
            msg.content === messageContent &&
            msg.id.startsWith('user-') &&
            Math.abs(msg.timestamp - new Date(newMessage.created_at).getTime()) < 5000 // 5 second tolerance
          );
          
          if (matchingOptimisticIndex !== -1) {
            console.log('🔄 SUBSCRIPTION: Replacing optimistic message at index:', matchingOptimisticIndex);
            
            // Replace the optimistic message with the persistent one
            const updatedMessages = [...prev];
            updatedMessages[matchingOptimisticIndex] = {
              id: newMessage.id,
              content: messageContent,
              type: 'user',
              timestamp: new Date(newMessage.created_at).getTime(),
              files: processedFiles.length > 0 ? processedFiles : prev[matchingOptimisticIndex].files || []
            };
            
            // Clean up optimistic tracking
            const optimisticId = prev[matchingOptimisticIndex].id;
            setOptimisticMessages(current => {
              const updated = new Map(current);
              updated.delete(optimisticId);
              return updated;
            });
            
            return updatedMessages;
          }
        }

        // No optimistic match found, add as new message
        const formattedMessage: Message = {
          id: newMessage.id,
          content: messageContent,
          type: (newMessage.role === 'user' ? 'user' : 'system') as 'user' | 'system',
          timestamp: new Date(newMessage.created_at).getTime(),
          files: processedFiles
        };

        console.log('🔄 SUBSCRIPTION: Final message for display:', {
          id: formattedMessage.id,
          type: formattedMessage.type,
          contentLength: formattedMessage.content.length,
          filesCount: formattedMessage.files?.length || 0,
          isFeedbackAnalysis: isFeedbackAnalysis(formattedMessage.content)
        });

        return [...prev, formattedMessage];
      });

      // Handle assistant responses
      if (newMessage.role === 'assistant') {
        console.log('🔄 SUBSCRIPTION: Assistant message received, clearing loading');
        setIsLoading(false);
        
        if (generatingTargetType) {
          console.log('🔄 SUBSCRIPTION: Target generation in progress, handling target extraction from assistant response');
          console.log('🔄 SUBSCRIPTION: Assistant message content preview:', messageContent.substring(0, 200));
          await handleTargetGeneration(messageContent);
        } else {
          console.log('🔄 SUBSCRIPTION: Assistant message received but no target generation in progress');
        }
        
        completeGeneration();
        await refreshTargetForSession();
      }
    });

    return () => {
      console.log('🔄 SUBSCRIPTION: Cleaning up subscription for session:', currentSessionId);
      subscription.unsubscribe();
    };
  }, [currentSessionId]); // Only depend on currentSessionId to prevent subscription restart during generation

  const handleTargetGeneration = async (responseContent: string, requestType?: string, sessionIdOverride?: string) => {
    try {
      console.log('🎯 ChatContext: Starting target generation with type:', requestType);
      
      const sessionIdToUse = sessionIdOverride || currentSessionId;
      
      if (requestType) {
        console.log('🎯 ChatContext: Generating target directly using services for type:', requestType);
        
        const directResult = await generateTargetByType(requestType, sessionIdToUse);
        
        if (directResult.success && directResult.targetCode && directResult.targetType) {
          console.log('✅ ChatContext: Target generation successful:', directResult);
          
          if (!sessionIdToUse) {
            console.error('❌ ChatContext: No session ID available for storing target');
            throw new Error('No session available for storing target');
          }
          
          // Prediction targets are already stored by the edge function (including the message)
          // Only store in targets table for image and location types
          if (directResult.targetType === 'prediction') {
            console.log('✅ ChatContext: Prediction target and message stored by edge function - message will arrive via real-time subscription');
            setCurrentTargetCode(directResult.targetCode);
            setTargetIsRevealed(false);
            // Don't manually add message - it will come through real-time subscription
            // from the message stored by the edge function
          } else {
            // Store image and location targets in the targets table
            const stored = await storeTargetSafely(
              sessionIdToUse,
              directResult.targetCode,
              directResult.targetType,
              directResult.targetUrl || '',
              directResult.unsplashData
            );
            
            if (stored) {
              setCurrentTargetCode(directResult.targetCode);
              setTargetIsRevealed(false);
              
              const systemMessage: Message = {
                id: `target-generated-${Date.now()}`,
                content: directResult.message || `Target generated: ${directResult.targetCode}`,
                type: "system",
                timestamp: Date.now()
              };
              setMessages(prev => [...prev, systemMessage]);
            } else {
              throw new Error('Failed to store target');
            }
          }
        } else {
          throw new Error(directResult.error || 'Target generation failed');
        }
        return;
      }

      const targetResult = extractTargetFromResponse(responseContent);
      if (targetResult.success && targetResult.targetCode && targetResult.targetType && targetResult.targetUrl) {
        console.log('🎯 ChatContext: Storing generated target from AI response:', targetResult);
        
        if (!sessionIdToUse) {
          console.error('❌ ChatContext: No session ID available for storing target');
          throw new Error('No session available for storing target');
        }
        
        const stored = await storeTargetSafely(
          sessionIdToUse,
          targetResult.targetCode,
          targetResult.targetType,
          targetResult.targetUrl,
          targetResult.unsplashData
        );
        if (stored) {
          setCurrentTargetCode(targetResult.targetCode);
          setTargetIsRevealed(false);
        }
      }
    } catch (error) {
      console.error('❌ ChatContext: Error handling target generation:', error);
      
      const errorMessage: Message = {
        id: `target-error-${Date.now()}`,
        content: `Failed to generate target: ${error.message}. Please try again.`,
        type: "system",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
      
      toast({
        title: "Target Generation Failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      completeGeneration();
    }
  };

  const refreshTargetForSession = async () => {
    if (!currentSessionId) return;
    
    try {
      const target = await ChatService.getTargetBySession(currentSessionId);
      if (target) {
        console.log('🎯 ChatContext: Refreshing target state:', {
          targetCode: target.target_code,
          currentCode: currentTargetCode,
          isRevealed: !!target.revealed_at,
          currentRevealed: targetIsRevealed
        });
        
        // Update target code if different
        if (target.target_code !== currentTargetCode) {
          console.log('🎯 ChatContext: Updated target code for session:', target.target_code);
          setCurrentTargetCode(target.target_code);
        }
        
        // Update reveal state if different
        const targetRevealed = !!target.revealed_at;
        if (targetRevealed !== targetIsRevealed) {
          console.log('🎯 ChatContext: Updated reveal state:', targetRevealed);
          setTargetIsRevealed(targetRevealed);
        }
      }
    } catch (error) {
      console.error('❌ ChatContext: Error refreshing target:', error);
    }
  };

  // Alias for consistency
  const refreshCurrentTarget = refreshTargetForSession;

  const createNewSession = async () => {
    try {
      console.log('🔄 ChatContext: Creating new session...');
      console.log('🔄 ChatContext: Current messages before new session:', messages.length);
      setCurrentTargetCode(null);
      setTargetIsRevealed(false);
      resetGeneration();
      const newSession = await createSession();
      console.log('🔄 ChatContext: CLEARING MESSAGES FOR NEW SESSION');
      setMessages([]);
      setIsNewSession(true); // Mark as new session (not default)
      console.log('✅ ChatContext: New session created');
      return newSession;
    } catch (error) {
      console.error('❌ ChatContext: Failed to create new session:', error);
      throw error;
    }
  };

  // ENHANCED: sendMessage with improved confirmation handling
  const sendMessage = async (message: string, files?: ProcessedFile[]) => {
    console.log('📤 SENDMESSAGE: Starting with params:', {
      messageLength: message.length,
      filesCount: files?.length || 0,
      fileDetails: files?.map(f => ({
        type: f.type,
        filename: f.filename,
        contentLength: f.content.length,
        isDataUrl: f.content.startsWith('data:image/'),
        isUrl: f.content.startsWith('http'),
        contentPreview: f.content.substring(0, 100)
      }))
    });

    if ((!message.trim() && (!files || files.length === 0)) || isLoading || !user) {
      console.log('📤 SENDMESSAGE: Blocking - invalid conditions');
      return;
    }

    if (!tokenWarningsInitialized) {
      console.log('💰 SENDMESSAGE: App not initialized, blocking');
      const initMessage: Message = {
        id: `init-${Date.now()}`,
        content: "Please wait for the app to finish loading before making requests.",
        type: "system",
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, initMessage]);
      return;
    }

    const estimatedTokens = Math.ceil(message.length / 4) + 500;
    
    console.log('💰 SENDMESSAGE: Token check - Balance:', tokenBalance, 'Required:', estimatedTokens);
    
    if (!checkTokensForRequest(estimatedTokens)) {
      console.log('💰 SENDMESSAGE: Insufficient tokens');
      addInsufficientTokensMessage(estimatedTokens);
      
      if (tokenBalance <= 0) {
        setShowTokenDepletionModal(true);
      }
      return;
    }

    // ENHANCED: Create optimistic message with proper file handling
    const optimisticId = `user-${Date.now()}`;
    const userMessage: Message = {
      id: optimisticId,
      content: message,
      type: "user",
      timestamp: Date.now(),
      files: files || []
    };

    console.log('📤 SENDMESSAGE: Adding optimistic message:', {
      id: userMessage.id,
      contentLength: userMessage.content.length,
      filesCount: userMessage.files?.length || 0,
      fileTypes: userMessage.files?.map(f => ({
        type: f.type,
        filename: f.filename,
        isBase64: f.content.startsWith('data:image/'),
        isUrl: f.content.startsWith('http')
      }))
    });
    
    // Add to optimistic tracking and state
    console.log('📤 SENDMESSAGE: Adding optimistic user message to state:', {
      id: userMessage.id,
      content: userMessage.content.substring(0, 100),
      currentMessagesCount: messages.length
    });
    setOptimisticMessages(prev => new Map(prev).set(optimisticId, userMessage));
    setMessages(prev => {
      console.log('📤 SENDMESSAGE: Messages before adding user message:', prev.length);
      const newMessages = [...prev, userMessage];
      console.log('📤 SENDMESSAGE: Messages after adding user message:', newMessages.length);
      return newMessages;
    });
    
    // Reset new session flag when first message is sent
    if (isNewSession) {
      setIsNewSession(false);
    }

    // Handle reveal requests - prevent sending to OpenAI
    if (isRevealRequest(message)) {
      console.log('🎯 ChatContext: REVEAL REQUEST DETECTED - Handling locally, not sending to OpenAI');
      resetGeneration();
      
      // Remove the optimistic message since we're handling this locally
      setOptimisticMessages(prev => {
        const updated = new Map(prev);
        updated.delete(optimisticId);
        return updated;
      });
      setMessages(prev => prev.filter(msg => msg.id !== optimisticId));
      
      const revealedTargetCode = await handleRevealRequest(currentSessionId, setMessages, setIsLoading, setTargetIsRevealed);

      if (revealedTargetCode) {
        setTargetIsRevealed(true);

        // Add analysis indicator message
        const analysisMessage: Message = {
          id: `analysis-start-${Date.now()}`,
          content: "🔍 Analyzing your impressions against the revealed target... This may take a moment.",
          type: "system",
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, analysisMessage]);

        console.log('🎯 ChatContext: Starting automatic analysis after target reveal');
        setIsLoading(true);
        
        try {
          const analysisResult = await handleTargetAnalysisRequest(
            "Analyze my impressions against the revealed target.",
            revealedTargetCode,
            currentSessionId
          );
          
          console.log('✅ ChatContext: Analysis completed:', analysisResult);
          
          // Check if the result indicates an error
          if (analysisResult.includes('error') || analysisResult.includes('Error') || analysisResult.includes('Insufficient')) {
            const errorMessage: Message = {
              id: `analysis-error-${Date.now()}`,
              content: analysisResult,
              type: "system",
              timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
            
            // Open purchase modal if insufficient tokens
            if (analysisResult.toLowerCase().includes('insufficient tokens') || analysisResult.includes('purchase more tokens')) {
              setShowTokenPurchaseModal(true);
            }
          }
        } catch (error) {
          console.error('❌ ChatContext: Analysis failed:', error);
          const errorMessage: Message = {
            id: `analysis-error-${Date.now()}`,
            content: "Failed to analyze your impressions. Please try again.",
            type: "system",
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, errorMessage]);
        } finally {
          // CRITICAL: Always clear loading state after analysis completes
          setIsLoading(false);
        }
      }
      return;
    }

    let targetType: string | null = null;
    let isExplicitTargetGeneration = false;
    
    // CRITICAL FIX: Check for target generation FIRST, before post-reveal routing
    if (!generatingTargetType && isTargetGenerationRequest(message)) {
      isExplicitTargetGeneration = startGeneration(message);
      if (isExplicitTargetGeneration) {
        targetType = getTargetTypeFromRequest(message);
        
        if (!targetType) {
          const clarificationMessage: Message = {
            id: `clarification-${Date.now()}`,
            content: "I'd be happy to generate a target for you! Please specify which type you'd like:\n\n• **Location target** - A random location with Street View imagery\n• **Image target** - A random photograph\n\nJust say \"location target\" or \"image target\" to proceed.",
            type: "system",
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, clarificationMessage]);
          completeGeneration();
          return;
        }
      }
    }

    // Handle target generation (works regardless of reveal state)
    if (isExplicitTargetGeneration && targetType) {
      console.log('🎯 ChatContext: Starting direct target generation for type:', targetType);
      
      // CRITICAL: Check tokens BEFORE target generation
      const estimatedTargetTokens = targetType === 'prediction' ? 2500 : 1500; // Predictions need more tokens for images
      console.log('💰 TARGET-GEN: Token check - Balance:', tokenBalance, 'Required:', estimatedTargetTokens);
      
      if (!checkTokensForRequest(estimatedTargetTokens)) {
        console.log('💰 TARGET-GEN: Insufficient tokens for target generation');
        
        // Remove optimistic user message
        setOptimisticMessages(prev => {
          const updated = new Map(prev);
          updated.delete(optimisticId);
          return updated;
        });
        setMessages(prev => prev.filter(msg => msg.id !== optimisticId));
        
        // Show error message
        const insufficientMessage: Message = {
          id: `insufficient-tokens-${Date.now()}`,
          content: `⚠️ **Insufficient Tokens**\n\nYou need approximately **${estimatedTargetTokens.toLocaleString()} tokens** to generate a ${targetType} target, but you only have **${tokenBalance.toLocaleString()} tokens**.\n\nPlease purchase more tokens to continue.`,
          type: "system",
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, insufficientMessage]);
        
        // Open the purchase modal
        setShowTokenPurchaseModal(true);
        
        completeGeneration();
        return;
      }
      
      // CRITICAL FIX: Don't create new session during target generation - use existing session
      let sessionIdToUse = currentSessionId;
      if (!sessionIdToUse) {
        console.error('❌ ChatContext: No session available for target generation - this should not happen');
        completeGeneration();
        setIsLoading(false);
        return;
      }
      
      // Keep the user's message visible during target generation
      setIsLoading(true);
      await handleTargetGeneration("", targetType, sessionIdToUse);
      return;
    }

    let sessionIdToUse = currentSessionId;
    if (!sessionIdToUse) {
      // 🛡️ CRITICAL FIX: Don't create new session during target generation
      if (generatingTargetType) {
        console.error('🛡️ ChatContext: BLOCKED session creation during target generation! Current target type:', generatingTargetType);
        setIsLoading(false);
        return;
      }
      
      try {
        console.log('🔄 ChatContext: No session found, creating new session...');
        const newSession = await createNewSession();
        sessionIdToUse = newSession.id;
      } catch (error) {
        console.error('❌ ChatContext: Failed to create session:', error);
        setIsLoading(false);
        return;
      }
    }

    // UPDATED: Handle post-reveal behavior - route messages differently based on reveal state
    if (targetIsRevealed) {
      console.log('📤 POST-REVEAL: Routing message to OpenAI for discussion (target already revealed)');
      
      // Post-reveal: Send directly to OpenAI for conversational discussion
      setIsLoading(true);
      try {
        // Extract already uploaded URLs from files (eager upload pattern)
        let fileUrls: string[] = [];
        if (files && files.length > 0) {
          fileUrls = files
            .filter(f => f.content.startsWith('http'))
            .map(f => f.content);
          
          console.log('📦 POST-REVEAL: Using uploaded file URLs:', fileUrls);
        }

        console.log('📤 POST-REVEAL: Calling OpenAI with:', {
          sessionId: currentSessionId,
          messageLength: message.length,
          fileUrlsCount: fileUrls.length
        });
        
        const result = await ChatService.sendMessage(currentSessionId!, message, fileUrls);
        console.log('📤 POST-REVEAL: OpenAI response received:', result);
        
      } catch (error) {
        console.error('❌ POST-REVEAL: Error sending to OpenAI:', error);
        setIsLoading(false);
        
        toast({
          title: "Failed to Send Message",
          description: error.message || "An error occurred while sending your message. Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    // PRE-REVEAL: Handle impressions with direct confirmation support
    if (files && files.length > 0 && currentTargetCode && !targetIsRevealed) {
      console.log('📝 ChatContext: Storing image impressions in target_notes (background)');
      
      Promise.all(files.map(async (file) => {
        if (file.type === 'image' && file.content.startsWith('http')) {
          try {
            const enhancedService = await import("../services/enhancedTargetNotesService");
            
            const response = await fetch(file.content);
            const blob = await response.blob();
            const fileObj = new File([blob], file.filename, { type: blob.type });
            
            const result = await enhancedService.enhancedTargetNotesService.storeImageNote(
              currentTargetCode,
              sessionIdToUse,
              fileObj,
              message.trim() || undefined
            );
            
            if (result) {
              console.log('✅ ChatContext: Image stored in target_notes');
            }
          } catch (error) {
            console.error('❌ ChatContext: Error storing in target_notes:', error);
          }
        }
      })).catch(error => {
        console.error('❌ ChatContext: Background target storage failed:', error);
      });
    }

    if (isFeedbackRequest(message)) {
      resetGeneration();
      setIsLoading(true);

      try {
        const analysisResult = await handleTargetAnalysisRequest(message, currentTargetCode, currentSessionId);
        console.log('✅ ChatContext: Manual analysis completed:', analysisResult);
      } catch (error) {
        console.error('❌ ChatContext: Manual analysis failed:', error);
        const errorMessage: Message = {
          id: `analysis-error-${Date.now()}`,
          content: "Failed to analyze your impressions. Please try again.",
          type: "system",
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
      }
      return;
    }

    if (generatingTargetType) {
      console.log('🔄 ChatContext: Blocking regular chat during target generation');
      return;
    }

    setIsLoading(true);
    let timeoutId: NodeJS.Timeout;
    
    try {
       // SIMPLIFIED: Extract already uploaded URLs from files (eager upload pattern)
      let fileUrls: string[] = [];
      if (files && files.length > 0) {
        console.log('📦 SENDMESSAGE: Processing files for API call:', {
          filesCount: files.length,
          fileDetails: files.map(f => ({
            type: f.type,
            filename: f.filename,
            isBase64: f.content.startsWith('data:image/'),
            isUrl: f.content.startsWith('http'),
            contentLength: f.content.length
          }))
        });

        // Extract URLs that were already uploaded during file selection (eager upload)
        fileUrls = files
          .filter(f => f.content.startsWith('http'))
          .map(f => f.content);
        
        console.log('📦 SENDMESSAGE: Using pre-uploaded URLs:', fileUrls);
        
        // If we have base64 data without URLs, include it for OpenAI analysis
        const hasBase64OnlyFiles = files.some(f => f.content.startsWith('data:image/'));
        if (hasBase64OnlyFiles && fileUrls.length === 0) {
          console.log('📦 SENDMESSAGE: Base64 fallback for OpenAI analysis');
          // Keep base64 data in message for OpenAI vision processing
        }
      }

      console.log('📤 SENDMESSAGE: Calling API with:', {
        sessionId: currentSessionId,
        messageLength: message.length,
        fileUrlsCount: fileUrls.length,
        fileUrls: fileUrls.map(url => url.substring(0, 100))
      });
      
      // Set up timeout before API call
      timeoutId = setTimeout(() => {
        console.log('⏰ SENDMESSAGE: Request timeout, clearing loading');
        setIsLoading(false);
        completeGeneration();
      }, 30000);
      
      const result = await ChatService.sendMessage(currentSessionId!, message, fileUrls);
      
      console.log('📤 SENDMESSAGE: API response received:', result);
      
      // Clear timeout on successful response
      clearTimeout(timeoutId);

      // ENHANCED: Handle direct confirmation responses from edge function
      if (result && typeof result === 'object' && result.confirmation) {
        console.log('✅ SENDMESSAGE: Direct confirmation received:', result.confirmation);
        
        // Add the confirmation message immediately
        const confirmationMessage: Message = {
          id: `confirmation-direct-${Date.now()}`,
          content: result.confirmation,
          type: "system",
          timestamp: Date.now()
        };
        
        // Track this confirmation to prevent subscription duplicates
        const confirmationKey = result.confirmation.substring(0, 50);
        setRecentConfirmations(prev => new Set([...prev, confirmationKey]));
        setTimeout(() => {
          setRecentConfirmations(prev => {
            const updated = new Set(prev);
            updated.delete(confirmationKey);
            return updated;
          });
        }, 10000);
        
        setMessages(prev => [...prev, confirmationMessage]);
        setIsLoading(false);
        return;
      }
      
      
      // Let subscription handler manage loading state for other responses

      if (typeof result === 'function') {
        return;
      }
    } catch (error) {
      console.error('❌ SENDMESSAGE: Error sending message:', error);
      // Clear timeout on error
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
      completeGeneration();
      
      if (error.message && error.message.includes('token')) {
        addInsufficientTokensMessage();
      } else {
        toast({
          title: "Failed to Send Message",
          description: error.message || "An error occurred while sending your message. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSwitchSession = async (sessionId: string) => {
    const formattedMessages = await switchSession(sessionId);
    setMessages(formattedMessages);
    setOptimisticMessages(new Map()); // Clear optimistic messages
    setIsNewSession(false); // Reset new session flag when switching to any session
    resetGeneration();
    // Don't reset targetIsRevealed here - let refreshCurrentTarget handle it
  };

  const handleTokenPurchaseClick = () => {
    setShowTokenDepletionModal(false);
    setShowTokenPurchaseModal(true);
  };

  return (
    <>
      <ChatContext.Provider 
        value={{ 
          messages, 
          sessions,
          sessionsList: sessions,
          currentSessionId,
          currentTargetCode,
          isLoading,
          isNewSession,
          isGenerating: generatingTargetType,
          generatingType,
          sendMessage,
          createNewSession,
          switchSession: handleSwitchSession,
          switchToSession: async (sessionId: string | null) => {
            if (sessionId) {
              await handleSwitchSession(sessionId);
            }
          },
          clearSession: () => {
            setMessages([]);
            setOptimisticMessages(new Map());
            setTargetIsRevealed(false);
            resetGeneration();
            clearSession();
          },
          targetIsRevealed: targetIsRevealed,
          addPredictionResultMessage: async (predictionTarget: any, userPrediction: 'YES' | 'NO') => {
            const resultContent = `PREDICTION_RESULT:${JSON.stringify({ predictionTarget, userPrediction })}`;
            
            // Add optimistic message immediately for UI
            const resultMessage: Message = {
              id: `prediction-result-${Date.now()}`,
              content: resultContent,
              type: 'system',
              timestamp: Date.now()
            };
            setMessages(prev => [...prev, resultMessage]);
            
            // PERSIST to database so it replays in historic view
            if (currentSessionId && user?.id) {
              try {
                const { supabase } = await import('@/integrations/supabase/client');
                const { error } = await supabase.from('messages').insert({
                  session_id: currentSessionId,
                  content: resultContent,
                  role: 'assistant',
                  user_id: user.id
                });
                
                if (error) {
                  console.error('⚠️ Failed to persist PREDICTION_RESULT message:', error);
                } else {
                  console.log('✅ PREDICTION_RESULT message persisted to database');
                }
              } catch (err) {
                console.error('❌ Error persisting PREDICTION_RESULT:', err);
              }
            }
          }
        }
      }>
        {children}
      </ChatContext.Provider>

      <TokenDepletionModal
        isOpen={showTokenDepletionModal && tokenWarningsInitialized && tokenBalance <= 0}
        onClose={() => setShowTokenDepletionModal(false)}
        onPurchaseClick={() => {
          setShowTokenDepletionModal(false);
          setShowTokenPurchaseModal(true);
        }}
      />

      <TokenPurchaseModal
        isOpen={showTokenPurchaseModal}
        onClose={() => setShowTokenPurchaseModal(false)}
        currentBalance={tokenBalance}
      />
    </>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

// Safe version that returns null when outside ChatProvider (for components used in both contexts)
export const useChatOptional = (): ChatContextType | null => {
  return useContext(ChatContext) ?? null;
};
